import React, { useEffect, useState } from "react";
import { LegacyStack, Text, Icon, Collapsible } from "@shopify/polaris";
import { CaretUpMinor, CaretDownMinor } from "@shopify/polaris-icons";

import CompletedBadge from "./CompletedBadge";
import NotStartedBadge from "./NotStartedBadge";
import styled from "styled-components";

const StyledCompletedBadge = styled.div`
  margin-right: 0;

  &[data-step-id="connect-store"] {
    margin-right: 2.2rem;
  }
`;

const StyledContainer = styled.div`
  cursor: pointer;

  &[data-step-id="connect-store"] {
    cursor: default;
  }
`;

export default function SetupStep({
  title,
  description,
  isStepCompleted,
  id,
  isOpen,
  statusBadgeTestId,
  children = undefined,
}: {
  title: string;
  description: string;
  isStepCompleted: boolean;
  id: string;
  isOpen?: boolean;
  statusBadgeTestId?: string;
  children?: React.ReactNode;
}) {
  const [isCollapsibleOpen, setIsCollapsibleOpen] = useState(isOpen ?? false);

  // Once async setup data arrives, we sync `isOpen` to open the migration step, if incomplete
  useEffect(() => {
    if (isOpen !== undefined) {
      setIsCollapsibleOpen(isOpen);
    }
  }, [isOpen]);

  // A step the merchant expanded manually keeps `isOpen` false, so collapse it on completion too
  useEffect(() => {
    if (isStepCompleted) {
      setIsCollapsibleOpen(false);
    }
  }, [isStepCompleted]);

  const handleToggle = () => setIsCollapsibleOpen(open => !open);

  return (
    <LegacyStack vertical spacing="extraTight">
      <StyledContainer data-step-id={id} onClick={handleToggle}>
        <LegacyStack distribution="equalSpacing">
          <Text variant="headingMd" as="p">
            {title}
          </Text>

          <LegacyStack>
            <StyledCompletedBadge data-step-id={id} data-testid={statusBadgeTestId}>
              {isStepCompleted ? <CompletedBadge /> : <NotStartedBadge />}
            </StyledCompletedBadge>

            {!!children && (
              <button
                aria-controls="install-collapsible-button"
                data-testid={id}
                style={{
                  all: "unset",
                  cursor: "pointer",
                }}
              >
                <Icon source={isCollapsibleOpen ? CaretUpMinor : CaretDownMinor} />
              </button>
            )}
          </LegacyStack>
        </LegacyStack>
      </StyledContainer>

      <Text as="span" variant="bodyMd" color="subdued">
        {description}
      </Text>

      {!!children && (
        <Collapsible open={isCollapsibleOpen} id={id}>
          <div style={{ paddingTop: "16px" }}>{children}</div>
        </Collapsible>
      )}
    </LegacyStack>
  );
}
